import React from "react";
import { connect } from "react-redux";
import { Button, Col, Form, Overlay, Popover, Row } from "react-bootstrap";
import PropTypes from "prop-types";

import Table from "../../../layout/table";
import Hamburger from "../../../layout/kebab";
import PersonSearch from "../../../layout/select/person-search";
import ConditionalButton from "../../../layout/conditional-button";
import Modal from "../../../layout/modal";
import { isGuid } from "../../../utils/string";
import { withNavigation } from "../../../utils/wrapper";
import { people as getCompanyPeople, peopleBatch } from "../../../actions/companies";
import { save as savePerson } from "../../../actions/people";

const emptyPersonModal = () => ({
    show: false,
    saving: false,
    error: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    fax: "",
    association: ""
});

class CompanyPeople extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            page: 0,
            size: 10,
            search: "",
            order: "lastName",
            index: 0,
            direction: "ASC",
            data: [{}],
            pages: 0,
            saving: false,
            menu: {},
            modal: {
                person: emptyPersonModal()
            }
        };
    }

    componentDidMount = async () => {
        await this.loadData();
    }

    componentDidUpdate = async (prevProps) => {
        if (prevProps.companyUuid !== this.props.companyUuid) {
            await this.loadData();
        }
    }

    normalizeRow = (row = {}) => {
        if (!row?.uuid && !row?.personUuid) return row;

        const uuid = row.personUuid || row.uuid;
        const displayName = row.displayName || [row.firstName, row.lastName].filter(Boolean).join(" ").trim();

        return {
            ...row,
            uuid,
            personUuid: uuid,
            displayName,
            association: row.association ?? row["Person_CompanyPerson.association"] ?? "",
            companyPersonUuid: row.companyPersonUuid || row["Person_CompanyPerson.uuid"] || null
        };
    };

    loadData = async () => {
        if (!isGuid(this.props.companyUuid)) {
            this.setState({ data: [{}], pages: 0 });
            return;
        }

        const result = await this.props.getCompanyPeople({
            companyUuid: this.props.companyUuid,
            page: this.state.page,
            size: this.state.size,
            order: this.state.order,
            direction: this.state.direction,
            search: this.state.search
        });

        const items = [{}, ...((result?.items || []).map(this.normalizeRow))];
        const count = Number(result?.count || 0);

        this.setState({
            data: items,
            pages: count > 0 ? Math.ceil(count / Number(this.state.size)) : 0
        });
    };

    hasChanges = () => {
        return (this.state.data || []).some((row) => row?.changed);
    };

    buildSavePayload = () => {
        return (this.state.data || [])
            .filter((row) => row?.changed && isGuid(row.personUuid || row.uuid))
            .map((row) => ({
                companyPersonUuid: row.companyPersonUuid || null,
                personUuid: row.personUuid || row.uuid,
                association: row.association || "",
                deleted: Boolean(row.deleted)
            }));
    };

    saveChanges = async () => {
        const items = this.buildSavePayload();
        if (!items.length) return { saved: 0, message: "No changes." };

        this.setState({ saving: true });

        try {
            const result = await this.props.peopleBatch({
                companyUuid: this.props.companyUuid,
                items
            });

            await this.loadData();
            return result;
        } finally {
            this.setState({ saving: false });
        }
    };

    onPersonSelect = (e) => {
        const person = e?.target?.item?.data;
        if (!person?.uuid) return;

        const newRow = this.normalizeRow({
            ...person,
            personUuid: person.uuid,
            association: "",
            companyPersonUuid: null,
            changed: true
        });

        this.setState((prev) => {
            const [, ...rest] = prev.data || [];
            const existing = rest.find((row) => row.uuid === newRow.uuid);
            const nextRow = existing ? { ...existing, deleted: false, changed: true } : newRow;

            return {
                data: [
                    {},
                    nextRow,
                    ...rest.filter((row) => row.uuid !== newRow.uuid)
                ]
            };
        });
    };

    onAssociationChange = (uuid, event) => {
        const association = event.target.value;

        this.setState((prev) => ({
            data: (prev.data || []).map((row) => {
                if (row.uuid !== uuid) return row;
                return { ...row, association, changed: true };
            })
        }));
    };

    openHamburger = (event, uuid) => {
        event.preventDefault();
        this.setState({
            menu: {
                show: true,
                uuid,
                target: event.currentTarget
            }
        });
    };

    closeHamburger = () => {
        this.setState((prev) => ({
            menu: {
                ...prev.menu,
                show: false,
                target: null
            }
        }));
    };

    onRowDelete = (uuid) => {
        this.setState((prev) => ({
            data: (prev.data || []).map((row) => {
                if (row.uuid !== uuid) return row;
                if (!row.companyPersonUuid) return { ...row, _removeFromUi: true };
                return { ...row, deleted: true, changed: true };
            }).filter((row) => !row._removeFromUi)
        }));
    };

    onRowRestore = (uuid) => {
        this.setState((prev) => ({
            data: (prev.data || []).map((row) => {
                if (row.uuid !== uuid) return row;
                return { ...row, deleted: false, changed: true };
            })
        }));
    };

    onCreatePersonClick = () => {
        this.setState((prev) => ({
            modal: {
                ...prev.modal,
                person: {
                    ...emptyPersonModal(),
                    show: true
                }
            }
        }));
    };

    onCreatePersonCancel = () => {
        this.setState((prev) => ({
            modal: {
                ...prev.modal,
                person: emptyPersonModal()
            }
        }));
    };

    onCreatePersonChange = (event) => {
        const { name, value } = event.target;

        this.setState((prev) => ({
            modal: {
                ...prev.modal,
                person: {
                    ...prev.modal.person,
                    [name]: value,
                    error: ""
                }
            }
        }));
    };

    get canCreatePerson() {
        const modal = this.state.modal.person;
        return [modal.firstName, modal.lastName].some((value) => String(value || "").trim().length > 0);
    }

    onCreatePersonOk = async () => {
        if (!this.canCreatePerson || this.state.modal.person.saving) return;

        const modal = this.state.modal.person;
        const displayName = [modal.firstName, modal.lastName].filter(Boolean).join(" ").trim();

        this.setState((prev) => ({
            modal: {
                ...prev.modal,
                person: {
                    ...prev.modal.person,
                    saving: true,
                    error: ""
                }
            }
        }));

        const result = await this.props.savePerson({
            displayName,
            firstName: modal.firstName,
            lastName: modal.lastName,
            email: modal.email,
            phone: modal.phone,
            fax: modal.fax
        });

        if (!result?.success || !isGuid(result.uuid)) {
            this.setState((prev) => ({
                modal: {
                    ...prev.modal,
                    person: {
                        ...prev.modal.person,
                        saving: false,
                        error: result?.message || "Unable to create person."
                    }
                }
            }));
            return;
        }

        const row = this.normalizeRow({
            ...result,
            personUuid: result.uuid,
            association: modal.association,
            changed: true
        });

        this.setState((prev) => {
            const [, ...rest] = prev.data || [];
            return {
                modal: {
                    ...prev.modal,
                    person: emptyPersonModal()
                },
                data: [
                    {},
                    row,
                    ...rest.filter((item) => item.uuid !== row.uuid)
                ]
            };
        });
    };

    onPersonFormat = (value, row) => {
        if (!isGuid(row?.uuid)) {
            return <PersonSearch id="companyPersonUuid" name="companyPersonUuid" onChange={this.onPersonSelect} />;
        }

        const label = row.displayName || [row.firstName, row.lastName].filter(Boolean).join(" ").trim() || "Untitled person";

        return (
            <Button
                variant="link"
                className={`p-0 ${row.deleted ? "text-decoration-line-through text-muted" : ""}`}
                onClick={() => this.props.navigate(`/dashboard/people/${row.uuid}`)}
            >
                {label}
            </Button>
        );
    };

    onAssociationFormat = (value, row) => {
        if (!isGuid(row?.uuid)) return null;

        return (
            <Form.Control
                type="text"
                value={row.association || ""}
                disabled={Boolean(row.deleted)}
                onChange={(event) => this.onAssociationChange(row.uuid, event)}
            />
        );
    };

    onContactFormat = (value, row) => {
        if (!isGuid(row?.uuid)) return null;

        return (
            <div className={row.deleted ? "text-muted text-decoration-line-through" : ""}>
                <div>{row.email || ""}</div>
                <div className="text-muted small">{row.phone || row.fax || ""}</div>
            </div>
        );
    };

    onActionFormat = (value, row) => {
        if (!isGuid(row?.uuid)) return null;
        return <Hamburger variant="link" vertical={false} onClick={(event) => this.openHamburger(event, row.uuid)} />;
    };

    onPageChange = (page) => {
        this.setState({ page: Number(page) }, this.loadData);
    };

    onSearch = (search) => {
        this.setState({ search, page: 0 }, this.loadData);
    };

    onSize = (size) => {
        this.setState({ size: Number(size), page: 0 }, this.loadData);
    };

    onColumnClick = (header) => {
        const index = this.headers.findIndex((item) => item.header === header.header && item.source === header.source);
        if (index < 0 || !header.sortable) return;

        this.setState((prev) => ({
            index,
            direction: prev.index === index && prev.direction === "DESC" ? "ASC" : "DESC",
            order: header.source,
            page: 0
        }), this.loadData);
    };

    get headers() {
        return [
            {
                header: "Person",
                sortable: true,
                source: "lastName",
                className: "text-muted text-small text-uppercase w-35",
                onRender: this.onPersonFormat
            },
            {
                header: "Association",
                sortable: true,
                source: "association",
                className: "text-muted text-small text-uppercase w-35",
                onRender: this.onAssociationFormat
            },
            {
                header: "Contact",
                sortable: false,
                source: "email",
                className: "text-muted text-small text-uppercase w-25",
                onRender: this.onContactFormat
            },
            {
                header: "",
                sortable: false,
                source: "uuid",
                className: "text-muted text-small text-uppercase w-5",
                onRender: this.onActionFormat
            }
        ];
    }

    renderCreatePersonModal = () => {
        const modal = this.state.modal.person;

        return (
            <Modal
                show={modal.show}
                title="Create Associated Person"
                size="lg"
                onCancel={this.onCreatePersonCancel}
                buttons={[
                    {
                        default: true,
                        caption: "cancel",
                        onClick: this.onCreatePersonCancel,
                        variant: "secondary",
                        enabled: !modal.saving,
                        display: true
                    },
                    {
                        caption: modal.saving ? "creating" : "create",
                        onClick: this.onCreatePersonOk,
                        variant: "outline-primary",
                        enabled: this.canCreatePerson && !modal.saving,
                        display: true
                    }
                ]}
            >
                {modal.error ? (
                    <div className="alert alert-danger mb-3">{modal.error}</div>
                ) : null}
                <Row className="g-3">
                    <Col md="6">
                        <div className="mb-3 top-label">
                            <Form.Control name="firstName" value={modal.firstName} onChange={this.onCreatePersonChange} autoFocus />
                            <Form.Label>FIRST NAME</Form.Label>
                        </div>
                    </Col>
                    <Col md="6">
                        <div className="mb-3 top-label">
                            <Form.Control name="lastName" value={modal.lastName} onChange={this.onCreatePersonChange} />
                            <Form.Label>LAST NAME</Form.Label>
                        </div>
                    </Col>
                    <Col md="6">
                        <div className="mb-3 top-label">
                            <Form.Control name="email" value={modal.email} onChange={this.onCreatePersonChange} />
                            <Form.Label>EMAIL</Form.Label>
                        </div>
                    </Col>
                    <Col md="3">
                        <div className="mb-3 top-label">
                            <Form.Control name="phone" value={modal.phone} onChange={this.onCreatePersonChange} />
                            <Form.Label>PHONE</Form.Label>
                        </div>
                    </Col>
                    <Col md="3">
                        <div className="mb-3 top-label">
                            <Form.Control name="fax" value={modal.fax} onChange={this.onCreatePersonChange} />
                            <Form.Label>FAX</Form.Label>
                        </div>
                    </Col>
                    <Col xs="12">
                        <div className="mb-3 top-label">
                            <Form.Control name="association" value={modal.association} onChange={this.onCreatePersonChange} />
                            <Form.Label>ASSOCIATION</Form.Label>
                        </div>
                    </Col>
                </Row>
            </Modal>
        );
    };

    render() {
        return (
            <>
                <Row className="g-3">
                    <Col xs="6">
                        <h2 className="small-title">People</h2>
                    </Col>
                    <Col xs="6" className="text-end">
                        <ConditionalButton
                            enabledVariant="outline-primary"
                            display
                            enabled={!this.state.saving}
                            onEnabledClick={this.onCreatePersonClick}
                            icon="plus"
                        >
                            New Person
                        </ConditionalButton>
                    </Col>
                    <Col xs="12">
                        <Table
                            headers={this.headers}
                            page={this.state.data}
                            sort={{ index: this.state.index, direction: this.state.direction }}
                            size={this.state.size}
                            index={this.state.page}
                            pages={this.state.pages}
                            onColumnClick={this.onColumnClick}
                            onPageChange={this.onPageChange}
                            onSearch={this.onSearch}
                            onSize={this.onSize}
                            searchable
                            sizable
                        />
                    </Col>
                </Row>

                <Overlay
                    target={this.state.menu.target}
                    show={this.state.menu.show}
                    placement="bottom-end"
                    rootClose
                    onHide={this.closeHamburger}
                >
                    {(props) => (
                        <Popover {...props}>
                            <Popover.Body>
                                <Row className="mb-2">
                                    <Col className="d-flex flex-row mt-1">
                                        <button
                                            type="button"
                                            className="btn btn-link p-0 me-2 btn-sm small text-start"
                                            onClick={() => {
                                                this.closeHamburger();
                                                this.props.navigate(`/dashboard/people/${this.state.menu.uuid}`);
                                            }}
                                        >
                                            Open
                                        </button>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col className="d-flex flex-row mt-1">
                                        <button
                                            type="button"
                                            className="btn btn-link p-0 me-2 btn-sm small text-start"
                                            onClick={() => {
                                                const row = (this.state.data || []).find((item) => item.uuid === this.state.menu.uuid);
                                                this.closeHamburger();
                                                if (row?.deleted) this.onRowRestore(row.uuid);
                                                else this.onRowDelete(this.state.menu.uuid);
                                            }}
                                        >
                                            {(this.state.data || []).find((item) => item.uuid === this.state.menu.uuid)?.deleted ? "Restore" : "Remove"}
                                        </button>
                                    </Col>
                                </Row>
                            </Popover.Body>
                        </Popover>
                    )}
                </Overlay>

                {this.renderCreatePersonModal()}
            </>
        );
    }
}

CompanyPeople.propTypes = {
    auth: PropTypes.object.isRequired,
    companyUuid: PropTypes.string
};

const mapStateToProps = (state) => ({
    auth: state.auth
});

export default withNavigation(
    connect(mapStateToProps, {
        getCompanyPeople,
        peopleBatch,
        savePerson
    }, null, { forwardRef: true })(CompanyPeople)
);
